import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useCareerSignals() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const signalsQuery = useQuery({
    queryKey: ["career_signals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("career_signals")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const userSignalsQuery = useQuery({
    queryKey: ["profile_career_signals", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_career_signals")
        .select("*")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data;
    },
  });

  const ensureUserSignals = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      const signals = signalsQuery.data;
      const existing = userSignalsQuery.data;
      if (!signals || !existing) return;

      const existingIds = new Set(existing.map((s) => s.signal_id));
      const missing = signals.filter((s) => !existingIds.has(s.id));

      if (missing.length === 0) return;

      const rows = missing.map((s) => ({
        user_id: user.id,
        signal_id: s.id,
        status: "not_started",
      }));

      const { error } = await supabase.from("profile_career_signals").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile_career_signals", user?.id] });
    },
  });

  const updateSignalStatus = useMutation({
    mutationFn: async ({ signalId, status }: { signalId: string; status: string }) => {
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("profile_career_signals")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("signal_id", signalId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile_career_signals", user?.id] });
    },
  });

  const recomputeProfileStatus = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      const userSignals = userSignalsQuery.data ?? [];
      const total = userSignals.length;
      if (total === 0) return;

      const done = userSignals.filter((s) => s.status === "done").length;
      const inProgress = userSignals.filter((s) => s.status === "in_progress").length;

      let profileStatus = "construction";
      if (done / total >= 0.7) {
        profileStatus = "ready";
      } else if (done > 0 || inProgress > 0) {
        profileStatus = "evolution";
      }

      const { error } = await supabase
        .from("profiles")
        .update({ profile_status: profileStatus })
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
    },
  });

  return {
    signals: signalsQuery.data ?? [],
    userSignals: userSignalsQuery.data ?? [],
    isLoading: signalsQuery.isLoading || userSignalsQuery.isLoading,
    ensureUserSignals,
    updateSignalStatus,
    recomputeProfileStatus,
  };
}
